import { GraphQLSearchSignedClass, AxiosError, MapResponseFn } from "@eco/client";
import { useEcoClient } from "hooks";
import { useCallback, useMemo } from "react";
import { UseEcoQuery, useEcoQuery } from ".";
import { UseEcoQueryOptions } from "./useEcoQuery";
import { useGrapqhlSignedQuery } from "./useGrapqhlSignedQuery";

/**
 * 
 * Custom hook to query from Ecomundo graphQL API
 * You can perform query within this hook
 * Onthology feature is supported too
 * Please refer to the documentation : https://redmine.ecomundo.tech/projects/it/wiki/GraphQL
 * Grapqhl sandbox is avalaible on "bo" application https://saka.ecomundo.tech/bo/
 * 
 * TData: The data expected from the query
 * TClass : The signed class that will ensure signature security and contain all query informations
 * TRawData : The expected response from the query, in purpose to transform it to TData using mapResponseFn
 * 
 */
export const useGraphQLSearch = <TData, TClass extends GraphQLSearchSignedClass, TRawData = unknown>(
        query: TClass,
        mapResponseFn?: MapResponseFn<TData, TRawData>,
        config?: UseEcoQueryOptions<unknown, TData, AxiosError>
    ): UseEcoQuery<TData, AxiosError> => {

    const ecoClient = useEcoClient();
    const cacheKey = useMemo(() => [query?.cacheKey, query?.variables], [query])
    const signedQuery = useGrapqhlSignedQuery(query)

    const queryFn = useCallback( async (): Promise<TData> => {
        return ecoClient.graphql<TData, TClass['variables'], TRawData>(signedQuery, mapResponseFn)
    }, [query])
    
    // call react-query with name of the query class as cache name
    return useEcoQuery<TData>(cacheKey, queryFn, { enabled: query !== undefined, ...config})
}
